// Dependencies
import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Container,
  Icon,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
// Components
import TableView, { ToobarButton, ExportButton } from "Components/TableView";
import ClipCard from "./.components/clipCard";
import ClipRequestForm from "./.components/clipRequestForm";
import ClipRequestAddNote from "./.components/clipRequestAddNote";
import { NotificationManager } from "Components/Notifications";
import moment from "moment";
import useExport from "Components/Table/Export";
import { getVidFleetClipStatus } from "Core/data/VideoEvents";
import { getUserFormattedDate } from "Core/data/Helpers";

import AdvancedFilters from "Modules/video/views/main/.components/advancedFilters";
import Preferences from "Core/containers/Preferences";

// Actions
import {
  clipRequestNewForm,
  // openVideoAdvancedFilters,
  openDeleteMediaDetailModal,
  openSharedMediaModal,
  sendGenericExport,
  clearRedux,
  openModalDeleteVideo,
} from "Redux/actions";

// Helpers
import { SliderRangePanel } from "Modules/.common/components/SliderRangePanel";
import Modal, { ModalBody } from "Components/Modal";
import "./styles.scss";
import TabInfo from "../tools/TabInfo";
import { PillTime } from "../tools/PillTime";
import { GetFormatForMoment, getInitialRange } from "Core/data/Helpers";
import DeleteVideoModal from "Modules/video/modals/delete";
import { searchClipRequestsQuery as searchClipsQuery, useRestoreClipRequests } from "Modules/video/.globals/api/clipRequests";
import { useClipsSearch, CLIPS_LIST_QUERY_KEY, DEFAULT_TIME_FRAME } from "./useClipsSearch";

export { CLIPS_LIST_QUERY_KEY };

export const ClipsTab = ({ tabName }) => {
  const { messages, locale } = useIntl();
  const dispatch = useDispatch();

  const { visibleForm, loadingCreateClip } = useSelector((state) => state.clipsRedux);

  const { user } = useSelector((state) => state.securityRedux);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags', 'videoDevices', 'unitVersionProduct'] });

  const { dateformat, timeZone } = user;
  const format = GetFormatForMoment(dateformat);

  const [filterOpen, setFilterOpen] = useState(false);

  const clipsSearch = useClipsSearch({ tabName });
  const { mutate: restoreClips } = useRestoreClipRequests({
    onError: () => NotificationManager.error(messages["errorMessage"], messages["error"], 3000, null, null, "warning"),
  });
  const {
    tableViewRef,
    allFilters,
    data,
    total,
    rowsPerPage,
    setRowsPerPage,
    loading,
    searchText,
    onSearchTextChange,
    selectedRows,
    setSelectedRows,
    removeFromSelection,
    handleChangePage,
    handleFiltersChange,
    handleInitialPreferences,
  } = clipsSearch;

  useEffect(() => {
    if (exportResult && exportName === "requestedClips") {
      NotificationManager.info(
        messages["exportInProcessBody"],
        messages["exportInProcessTitle"],
        4000,
        null,
        null,
        "info",
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }
  }, [exportResult, exportName]);

  const getHeaders = () => {
    return [
      messages["videoClipRequest_unit"],
      messages["videoClipRequest_driver"],
      messages["videoClipRequest_status"],
      messages["videoClipRequest_dateTime"],
    ];
  };

  const getDataToExport = (rows) => {
    let data = [];

    rows.forEach((row) => {
      const unitlabel = row?.label || "";
      const driverName = row?.driverName || "";
      const clipStatus = getVidFleetClipStatus(row?.status) || "";
      const status = messages[clipStatus?.lanKey] || "";
      const startDate = moment.unix(row?.startDate / 1000).format("llll") || "";

      data.push([unitlabel, driverName, status, startDate]);
    });

    return data;
  };

  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    [],
    selectedRows || [],
    null,
    `requestedClips-${moment().unix()}`,
    "landscape",
    true
  );

  const getClipStatus = useCallback(() => {
    return `
      let states = {
        "requestAccepted": '${messages["videoClipRequest_pending"]}',
        "startUpload": '${messages["videoClipRequest_inProgress"]}',
        "uploading": '${messages["videoClipRequest_inProgress"]}',
        "requestDecline": '${messages["videoClipRequest_failed"]}',
        "failed": '${messages["videoClipRequest_failed"]}',
        "timeout": '${messages["videoClipRequest_failed"]}',
        "interrupted": '${messages["videoClipRequest_failed"]}',
        "requestPending": '${messages["videoClipRequest_inProgress"]}',
        "finished": '${messages["videoClipRequest_completed"]}',
        "mp4RemuxFinished": '${messages["videoClipRequest_completed"]}',
        "unknown": '${messages["videoClipRequest_unknown"]}',
      };

      return states[row.status] || '${messages["videoClipRequest_unknown"]}';
    `;
  });

  const getLabel = useCallback((selectedRow) => {
    const { startDate, unit } = selectedRow;
    return `${moment
      .unix(startDate / 1000)
      .locale(locale)
      .format(`${format} HH:mm:ss`)}, ${unit?.label || selectedRow?.label || '....'}
      `;
  });

  const getUnitByList = (row) => {
    let search;

    if (!row || !units?.length) return search;

    const deviceId = Number(row.deviceId);
    const esn = row.esn;

    search = units.find(
      ({ id, esn: unitEsn, videoDevices }) =>
        Number(id) === deviceId ||
        unitEsn === esn ||
        videoDevices?.some(({ esn: videoEsn }) => videoEsn === esn)
    );

    return search;
  };

  return (
    <Preferences
      preferencesKey={tabName}
      onInitialPreferences={handleInitialPreferences}
    >
      <ClipRequestForm
        newClipRequestOpen={clipRequestNewForm}
        unitList={units}
      />

      <ClipRequestAddNote messages={messages} range={getInitialRange(allFilters?.timeFrame || DEFAULT_TIME_FRAME, allFilters.dateRange?.start, allFilters.dateRange?.end)} />

      <Container
        maxWidth={false}
        style={{ height: "100%", paddingBottom: 24, paddingLeft: 12 }}
      >
        <Box
          width="100%"
          display="flex"
          mb="30px"
          alignItems={"center"}
          justifyContent="space-between"
        >
          <Box width="20%">
            <ToobarButton
              icon="add_box"
              tooltip={messages["table_add"]}
              onClick={() => dispatch(clipRequestNewForm(true))}
            />

            <ExportButton
              className="media-events-icon-download"
              style={{ color: "black" }}
              onExport={(type) => {
                if (selectedRows?.length) {
                  if (type === "CSV") {
                    exportCSVPdf.exportCSV();
                  } else if (type === "PDF") {
                    exportCSVPdf.exportPDF();
                  }

                  tableViewRef.current?.setSelectedRows([]);
                  setSelectedRows([]);

                  NotificationManager.success(
                    messages["exportSuccess"],
                    messages["success"],
                    4000,
                    null,
                    null,
                    "success",
                    null
                  );
                } else {

                  let events = [];
                  allFilters?.eventsType?.forEach((event) => {
                    events.push(event?.label);
                  });

                  const tableHeader = [
                    {
                      name: messages["videoClipRequest_requested"],
                      value: "",
                    },
                    {
                      name: messages["videoClipRequest_filters"],
                      value: "",
                    },
                    {
                      name: messages["videoClipRequest_unitsFilter"],
                      value: "",
                    },
                    {
                      name: messages["videoClipRequest_dateRange"],
                      value: `${getUserFormattedDate(
                        user,
                        allFilters?.dateRange?.start,
                        messages,
                        "h:mm A",
                        false,
                        true
                      )} - ${getUserFormattedDate(
                        user,
                        allFilters?.dateRange?.end,
                        messages,
                        "h:mm A",
                        false,
                        true
                      )}`,
                    },
                  ];

                  if (allFilters?.hasNotes) {
                    tableHeader.push({
                      name: messages["videoClipRequest_hasNotes"],
                      value: "",
                    });
                  }

                  const variables = {
                    ...allFilters,
                  };

                  const exportColumns = [
                    {
                      header: messages["videoMainView_unit"],
                      column: "label",
                      functionString: 'return row?.label || ""',
                    },
                    {
                      header: `${messages["videoMainView_driver"]} / ${messages["settingsDriver_externalId"]}`,
                      column: "driver",
                      functionString:
                        `return row?.driverName ? row?.driverName + " / " + (row?.driverExternalId || "") : "${messages["unassigned"]}"`,
                    },
                    {
                      header: messages["videoClipRequest_status"],
                      column: "event",
                      functionString: getClipStatus(),
                    },
                    {
                      header: messages["videoClipRequest_dateTime"],
                      column: "dateTime",
                      functionString:
                        'return moment.unix(row?.startDate / 1000).format("llll") || ""',
                    },
                    {
                      header: messages["videoClipRequest_notes"],
                      column: "notes",
                      functionString: 'return row?.notes || ""',
                    },
                  ];

                  dispatch(
                    sendGenericExport(
                      "requestedClips",
                      type === "CSV" ? "Csv" : "Pdf",
                      searchClipsQuery,
                      variables,
                      exportColumns,
                      {
                        unit: "pt",
                        size: "A4",
                        orientation: "landscape",
                        fontSize: 10,
                      },
                      tableHeader,
                      null,
                      null,
                      {
                        1: { cellWidth: 140 },
                        6: { cellWidth: 140 },
                      }
                    )
                  );
                }
              }}
            />

            <ToobarButton
              icon="filter_list"
              tooltip={messages["table_filter"]}
              onClick={() => setFilterOpen(true)}
            />

            {selectedRows.length !== 0 && (
              <>
                {allFilters?.category !== "deleted" && (
                  <ToobarButton
                    icon="delete"
                    tooltip={messages["delete"]}
                    onClick={() => {
                      let items = [];
                      selectedRows?.forEach((selectedRow) => {
                        const { id } = selectedRow;
                        items.push({
                          id: id,
                          label: getLabel(selectedRow),
                        });
                      });

                      //dispatch(openVideoAdvancedFilters(false));
                      dispatch(
                        openModalDeleteVideo({
                          type: "clip",
                          items: items,
                        })
                      );
                    }}
                    disabled={selectedRows.length === 0}
                  />
                )}
                <ToobarButton
                  icon="share"
                  tooltip={messages["videoMainView_share"]}
                  disabled={
                    selectedRows.length === 0 ||
                    (selectedRows.find(
                      (selectedRow) =>
                        selectedRow?.status !== "finished" &&
                        selectedRow?.status !== "mp4RemuxFinished"
                    )
                      ? true
                      : false)
                  }
                  onClick={() => {
                    let items = [];
                    selectedRows.forEach((selectedRow) => {
                      const { id, deviceId } = selectedRow;
                      items.push({
                        entity: {
                          id: id,
                          name: "ClipRequest",
                          label: getLabel(selectedRow),
                        },
                        deviceId: deviceId,
                      });
                    });

                    dispatch(
                      openSharedMediaModal({
                        open: true,
                        items: items,
                      })
                    );
                  }}
                />
                {allFilters?.category === "deleted" && (
                  <ToobarButton
                    icon="restore_from_trash"
                    tooltip={messages["videoMainView_restore"]}
                    disabled={selectedRows.length === 0}
                    onClick={() => {
                      restoreClips(
                        selectedRows.map((clip) => clip.id),
                        { onSuccess: () => setSelectedRows([]) }
                      );
                    }}
                  />
                )}
              </>
            )}
          </Box>
          <Box
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Box
              style={{
                width: "60%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <PillTime
                dateRange={allFilters?.dateRange}
                timeFrame={allFilters?.timeFrame || DEFAULT_TIME_FRAME}
                timezone={timeZone}
                format={format}
                onClick={() => {
                  // dispatch(openVideoAdvancedFilters(!openFilters, "Clips"));
                  setFilterOpen(true);
                  dispatch(openSharedMediaModal({ open: false }));
                  dispatch(openDeleteMediaDetailModal({ open: false }));
                }}
              />
            </Box>

            <Box style={{ width: "40%" }}>
              <OutlinedInput
                disabled={loading}
                id={"custom-search"}
                placeholder={messages["videoClipRequest_search"]}
                type={"text"}
                value={searchText || ""}
                fullWidth={true}
                onChange={(e) => {
                  onSearchTextChange(e.target.value);
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="placeholder"
                      style={{ fontSize: 25, color: "black" }}
                    >
                      <Icon>search</Icon>
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Box>
        </Box>
        <Box style={{ margin: "-20px 0 20px 0" }}>
          <TabInfo />
        </Box>
        <TableView
          ref={tableViewRef}
          data={data || []}
          loading={loading}
          rowsPerPage={rowsPerPage}
          name="Table View"
          topSpaceSize={330}
          sizeInput={3}
          viewType="card"
          messages={messages}
          inputSearch={false}
          hideAllInPagination
          renderRow={(row) => (
            <div style={{ marginBottom: "10px" }} data-testid="card-view-row-clips-container" key={row?.id}>
               <ClipCard
              row={row}
              getLabel={getLabel}
              messages={messages}
              unit={getUnitByList(row)}
              hiddenDeleteButton={allFilters?.category === "deleted"}
            />
            </div>
          )}
          toolbarContainerClassName="table-toolbar-container"
          total={total}
          onChangePage={(page) => {
            handleChangePage(page);
          }}
          serverSide
          onChangeRowsPerPage={(newRowsPerPage, newPage) => {
            setRowsPerPage(newRowsPerPage);
            handleChangePage(newPage, newRowsPerPage);
          }}
          setSelectedRows={(rows) => {
            setSelectedRows(rows);
          }}
        >


        </TableView>
        <Modal
          open={visibleForm}
          loading={loadingCreateClip}
          className="slider-clip"
          size="large"
        >
          <ModalBody className="clip-request-modal-body">
            <SliderRangePanel />
          </ModalBody>
        </Modal>

        <DeleteVideoModal
          onDeleted={removeFromSelection}
          onClose={(refresh, type) => {
            if (refresh) {
              tableViewRef?.current?.setNewPage(1);
              handleChangePage(1);
            }
          }}
        />
      </Container>

      <AdvancedFilters
        open={filterOpen}
        currentFilters={allFilters}
        setOpen={setFilterOpen}
        indexTab={1}
        isClipRequest={true}
        onChangeFilters={handleFiltersChange}
      //setPreferences={setPreferences}
      />
    </Preferences>
  );
};

export default ClipsTab;
